import React, { Component } from 'react';
import moment from 'moment';
import { HTTPService } from "../../../services/httpService";

const API = new HTTPService();

class CampaignDetails extends Component {
  state = {
    showDeleteConfirm: false,
    statistics: null,
    loadingStats: true,
    sendingPreview: false
  };

  handleDeleteClick = () => {
    this.setState({ showDeleteConfirm: true });
  };

  handleDeleteConfirm = () => {
    this.props.onDelete(this.props.campaign.id);
    this.setState({ showDeleteConfirm: false });
  };

  handleDeleteCancel = () => {
    this.setState({ showDeleteConfirm: false });
  };

  async componentDidMount() {
    await this.loadStatistics();
  }

  async componentDidUpdate(prevProps) {
    // Reload statistics if campaign changes
    if (prevProps.campaign?.id !== this.props.campaign?.id) {
      await this.loadStatistics();
    }
  }

  loadStatistics = async () => {
    const { campaign, installerId } = this.props;

    if (!campaign || !campaign.id) {
      return;
    }

    this.setState({ loadingStats: true });

    try {
      const response = await API.get(`/campaigns/${campaign.id}/statistics?installer_id=${installerId}`);

      if (response.data) {
        this.setState({
          statistics: response.data,
          loadingStats: false
        });
      } else {
        this.setState({
          statistics: null,
          loadingStats: false
        });
      }
    } catch (error) {
      console.error("Error loading campaign statistics:", error);
      // If endpoint doesn't exist yet or returns error, show default values
      this.setState({
        statistics: null,
        loadingStats: false
      });
    }
  };

  handleToggleStatus = () => {
    const { campaign } = this.props;
    const newStatus = campaign.campaign_status === "active" ? "paused" : "active";

    if (!window.confirm(
      `Are you sure you want to turn this automation ${newStatus === "active" ? "on" : "off"}?\n\n${
        newStatus === "paused"
          ? "This automation will stop running and no emails will be sent."
          : "This automation will resume running daily at 2:00 AM ET."
      }`
    )) {
      return;
    }

    this.props.onToggleStatus(campaign.id, newStatus);
  };

  handleActivate = () => {
    const { campaign } = this.props;
    const deliveryTime = campaign.delivery_time || "09:00";

    if (!window.confirm(
      `Are you sure you want to turn this automation on?\n\nThis automation will start running daily at ${deliveryTime} and emails will be sent to matching customers.`
    )) {
      return;
    }

    this.props.onToggleStatus(campaign.id, "active");
  };

  // Send preview email
  sendPreviewEmail = async () => {
    const { campaign, installerEmail } = this.props;

    if (!campaign.message_subject || !campaign.message_body_html) {
      alert("This automation doesn't have a complete email message configured. Please edit the automation to add subject and body.");
      return;
    }

    if (!installerEmail) {
      alert("Unable to send preview email: Installer email not found");
      return;
    }

    this.setState({ sendingPreview: true });

    try {
      const response = await API.post('/campaigns/preview-email', {
        preview_email: installerEmail,
        message_subject: campaign.message_subject,
        message_body_html: campaign.message_body_html,
        cta_label: campaign.cta_label || null,
        cta_url: campaign.cta_url || null,
        campaign_type: campaign.campaign_type
      });

      if (response.data && response.data.success) {
        alert(`Preview email sent to ${installerEmail}! Check your inbox.`);
      }
    } catch (error) {
      console.error("Error sending preview email:", error);
      alert(`Error sending preview email: ${error.message || 'Unknown error'}`);
    } finally {
      this.setState({ sendingPreview: false });
    }
  };

  // Get audience type from customer_type field in rules
  getAudienceType = () => {
    let rules = this.props.campaign.rules_json || {};

    // Parse rules_json if it's a string
    if (typeof rules === 'string') {
      try {
        rules = JSON.parse(rules);
      } catch (e) {
        console.error('Error parsing rules_json:', e);
        rules = {};
      }
    }

    if (Array.isArray(rules.customer_type)) {
      if (rules.customer_type.includes('customer')) {
        return "customers";
      }
      if (rules.customer_type.includes('prospect')) {
        return "prospects";
      }
      if (rules.customer_type.includes('request')) {
        return "request";
      }
    }

    // Default to customers
    return "customers";
  };

  // Format rules for display
  getActiveRules = () => {
    let rules = this.props.campaign.rules_json || {};

    // Parse rules_json if it's a string
    if (typeof rules === 'string') {
      try {
        rules = JSON.parse(rules);
      } catch (e) {
        console.error('Error parsing rules_json:', e);
        rules = {};
      }
    }

    const activeRules = [];
    const audienceType = this.getAudienceType();

    const isValidValue = (value) => {
      return value !== null &&
             value !== undefined &&
             value !== "" &&
             value !== "null" &&
             value !== 0;
    };

    // System campaigns - show trigger information (match wizard preview format)
    if (audienceType === "request") {
      const stateLabels = {
        'created': 'estimate is created',
        'scheduled': 'job is scheduled to start',
        'collected': 'vehicle is collected'
      };

      if (rules.trigger_state && rules.trigger_offset_days) {
        const absDay = Math.abs(rules.trigger_offset_days);
        const dayText = absDay === 1 ? 'day' : 'days';
        const timing = rules.trigger_offset_days < 0 ? 'before' : 'after';
        const eventLabel = stateLabels[rules.trigger_state] || rules.trigger_state;

        activeRules.push(`${absDay} ${dayText} ${timing} ${eventLabel}`);
      } else {
        activeRules.push("Trigger not configured");
      }

      return activeRules;
    }

    // Backend uses days_since_created_min/max for BOTH customers and prospects
    const minDays = rules.days_since_created_min;
    const maxDays = rules.days_since_created_max;

    if (audienceType === "prospects") {
      if (isValidValue(minDays)) {
        activeRules.push(`At least ${minDays} days since added`);
      }
      if (isValidValue(maxDays)) {
        activeRules.push(`No more than ${maxDays} days since added`);
      }

      if (activeRules.length === 0) {
        activeRules.push("All prospects (no date filters)");
      }
    }

    if (audienceType === "customers") {
      if (isValidValue(minDays)) {
        activeRules.push(`At least ${minDays} days since last service`);
      }
      if (isValidValue(maxDays)) {
        activeRules.push(`No more than ${maxDays} days since last service`);
      }

      // Service catalog
      if (Array.isArray(rules.service_catalog) && rules.service_catalog.length > 0) {
        activeRules.push(`Service types: ${rules.service_catalog.join(', ')}`);
      } else {
        if (activeRules.length > 0) {
          activeRules.push(`All service types`);
        }
      }

      if (activeRules.length === 0) {
        activeRules.push("All customers (no filters)");
      }
    }

    return activeRules;
  };

  render() {
    const { campaign, onEdit, onCancel } = this.props;
    const { showDeleteConfirm, statistics, loadingStats, sendingPreview } = this.state;

    if (!campaign || !campaign.id) {
      return null;
    }

    const audienceType = this.getAudienceType();
    const activeRules = this.getActiveRules();

    return (
      <React.Fragment>
        {/* Header */}
        <div className="shadow-sm p-3 corners detail-panel-selected">
          <button 
            type="button" 
            className="close d-none d-lg-block" 
            onClick={onCancel} 
            aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="text-center">
            <h4 className="mb-0">Automation Details</h4>
          </div>
        </div>

        {/* Content */}
        <div className="shadow-sm p-4 corners bg-white">
          
          {/* Campaign Info Section - Combined with Target Audience */}
          <div className="card mb-3">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <strong><i className="fa fa-info-circle"></i> Setup</strong>
              <div>
                <span className={
                  campaign.campaign_type === "always_on"
                    ? "badge badge-primary mr-2"
                    : campaign.campaign_type === "system"
                    ? "badge badge-info mr-2"
                    : "badge badge-warning mr-2"
                }>
                  {campaign.campaign_type === "always_on"
                    ? "Always-On"
                    : campaign.campaign_type === "system"
                    ? "CX Communication"
                    : "Promotional"}
                </span>
                <span className={
                  campaign.campaign_status === "active"
                    ? "badge badge-success"
                    : campaign.campaign_status === "completed"
                    ? "badge badge-info"
                    : campaign.campaign_status === "draft"
                    ? "badge badge-secondary"
                    : "badge badge-warning"
                }>
                  {campaign.campaign_status === "active" && "Status: On"}
                  {campaign.campaign_status === "paused" && "Status: Off"}
                  {campaign.campaign_status === "draft" && "Status: Off"}
                  {campaign.campaign_status === "completed" && "Status: Completed"}
                </span>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <p className="mb-2">
                    <strong>Automation Name:</strong><br />
                    <span className="text-muted">{campaign.campaign_name}</span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="mb-2">
                    <strong>Purpose:</strong><br />
                    <span className={
                      campaign.campaign_type === "always_on"
                        ? "badge badge-primary badge-lg"
                        : campaign.campaign_type === "system"
                        ? "badge badge-info badge-lg"
                        : "badge badge-warning badge-lg"
                    }>
                      {campaign.campaign_type === "always_on"
                        ? "Always-On"
                        : campaign.campaign_type === "system"
                        ? "CX Communication"
                        : "Promotional"}
                    </span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="mb-2">
                    <strong>Runs Under:</strong><br />
                    <span className="text-muted">
                      {campaign.campaign_type === "system"
                        ? "Iris – Customer Experience"
                        : "Luke – Re-Engagement"}
                    </span>
                  </p>
                </div>
              </div>

              {campaign.campaign_description && (
                <div className="row mt-2">
                  <div className="col-12">
                    <p className="mb-2">
                      <strong>Automation Description:</strong><br />
                      <span className="text-muted">{campaign.campaign_description}</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="row mt-2">
                <div className="col-md-4">
                  <p className="mb-2">
                    <strong>Target Audience:</strong><br />
                    <span className={
                      audienceType === "customers"
                        ? "badge badge-success badge-lg"
                        : audienceType === "prospects"
                        ? "badge badge-info badge-lg"
                        : "badge badge-warning badge-lg"
                    }>
                      {audienceType === "customers"
                        ? "Customers"
                        : audienceType === "prospects"
                        ? "Prospects"
                        : "Requests"}
                    </span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="mb-2">
                    <strong>Last Run:</strong><br />
                    <span className="text-muted">
                      {campaign.last_run_at
                        ? moment(campaign.last_run_at).format("MMM D, YYYY h:mm A")
                        : "Never"
                      }
                    </span>
                  </p>
                </div>
              </div>
              
              {/* Targeting Rules / Trigger Configuration - moved from separate section */}
              <div className="row mt-3">
                <div className="col-12">
                  {activeRules.length > 0 ? (
                    <React.Fragment>
                      <p className="mb-2">
                        <strong>When It Sends:</strong>
                      </p>
                      <ul className="mb-0">
                        {activeRules.map((rule, index) => (
                          <li key={index} className="text-muted mb-1">
                            <i className={audienceType === "request" ? "fa fa-clock-o text-primary" : "fa fa-filter text-primary"}></i> {rule}
                          </li>
                        ))}
                      </ul>
                    </React.Fragment>
                  ) : (
                    <p className="text-muted mb-0">
                      <i className="fa fa-exclamation-triangle text-warning"></i> {audienceType === "request" ? "No trigger configured" : "No targeting rules set"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Section - moved above email */}
          <div className="card mb-3">
            <div className="card-header bg-light">
              <strong><i className="fa fa-bar-chart"></i> Activity</strong>
            </div>
            <div className="card-body">
              {loadingStats ? (
                <div className="text-center py-4">
                  <i className="fa fa-spinner fa-spin fa-2x text-muted"></i>
                  <p className="text-muted mt-2">Loading statistics...</p>
                </div>
              ) : (
                <div className="row text-center">
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-repeat fa-2x text-primary"></i>
                    </div>
                    <h5 className="mb-0">{statistics?.total_runs || 0}</h5>
                    <small className="text-muted">Sends</small>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-paper-plane fa-2x text-success"></i>
                    </div>
                    <h5 className="mb-0">{statistics?.emails_sent || 0}</h5>
                    <small className="text-muted">Emails Sent</small>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-envelope-open fa-2x text-info"></i>
                    </div>
                    <h5 className="mb-0">
                      {statistics?.opens || 0}
                      {statistics?.emails_sent > 0 && (
                        <small className="d-block text-muted" style={{ fontSize: '12px' }}>
                          ({Math.round((statistics.opens / statistics.emails_sent) * 100)}%)
                        </small>
                      )}
                    </h5>
                    <small className="text-muted">Opens</small>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-mouse-pointer fa-2x text-warning"></i>
                    </div>
                    <h5 className="mb-0">
                      {statistics?.clicks || 0}
                      {statistics?.emails_sent > 0 && (
                        <small className="d-block text-muted" style={{ fontSize: '12px' }}>
                          ({Math.round((statistics.clicks / statistics.emails_sent) * 100)}%)
                        </small>
                      )}
                    </h5>
                    <small className="text-muted">Clicks</small>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-file-text fa-2x text-primary"></i>
                    </div>
                    <h5 className="mb-0">{statistics?.requests_generated || 0}</h5>
                    <small className="text-muted">Requests</small>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-2">
                      <i className="fa fa-dollar fa-2x text-success"></i>
                    </div>
                    <h5 className="mb-0">
                      {statistics?.revenue_generated
                        ? `$${statistics.revenue_generated.toLocaleString()}`
                        : '$0'}
                    </h5>
                    <small className="text-muted">Revenue</small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Campaign Offer Section - Only show if promo code or discount fields are set */}
          {(campaign.promo_code || campaign.discount_description) && (
            <div className="card mb-3">
              <div className="card-header bg-light">
                <strong><i className="fa fa-gift"></i> Campaign Offer</strong>
              </div>
              <div className="card-body">
                <div className="row">
                  {campaign.promo_code && (
                    <div className="col-md-6">
                      <p className="mb-2"><strong>Promo Code:</strong></p>
                      <span className="badge badge-success" style={{ fontSize: '16px', padding: '8px 16px' }}>
                        {campaign.promo_code}
                      </span>
                    </div>
                  )}
                  {campaign.discount_description && (
                    <div className="col-md-6">
                      <p className="mb-2"><strong>Discount:</strong></p>
                      <span className="text-muted">{campaign.discount_description}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fa fa-info-circle"></i> This information will be shown to customers in the chatbot and added to estimate notes. Front desk applies discounts manually.
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="mb-2 mb-md-0">
              {/* Hide Edit and Send Preview for completed campaigns */}
              {campaign.campaign_status !== "completed" && (
                <>
                  <button
                    type="button"
                    className="btn btn-success mr-2"
                    onClick={onEdit}>
                    EDIT
                  </button>

                  <button
                    type="button"
                    className="btn btn-info mr-2"
                    onClick={this.sendPreviewEmail}
                    disabled={sendingPreview}
                    title="Send a test email to your inbox">
                    {sendingPreview ? (
                      <>
                        <i className="fa fa-spinner fa-spin"></i> Sending...
                      </>
                    ) : (
                      <>
                        SEND PREVIEW
                      </>
                    )}
                  </button>
                </>
              )}

              {campaign.campaign_status === "draft" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.handleActivate}>
                  TURN ON
                </button>
              )}

              {campaign.campaign_status === "active" && (
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={this.handleToggleStatus}>
                  TURN OFF
                </button>
              )}

              {campaign.campaign_status === "paused" && (
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={this.handleToggleStatus}>
                  TURN ON
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn btn-danger"
              onClick={this.handleDeleteClick}>
              DELETE
            </button>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <React.Fragment>
              <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header bg-danger text-white">
                      <h5 className="modal-title">
                        <i className="fa fa-exclamation-triangle"></i> Delete Automation?
                      </h5>
                      <button
                        type="button"
                        className="close text-white"
                        onClick={this.handleDeleteCancel}>
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      <p className="mb-3">
                        Are you sure you want to delete <strong>"{campaign.campaign_name}"</strong>?
                      </p>
                      <div className="alert alert-warning mb-0">
                        <i className="fa fa-warning"></i> <strong>This cannot be undone.</strong>
                        <br />
                        All automation data will be removed.
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={this.handleDeleteCancel}>
                        <i className="fa fa-times"></i> Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={this.handleDeleteConfirm}>
                        <i className="fa fa-trash"></i> Delete Automation
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </React.Fragment>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default CampaignDetails;